import {useCallback, useMemo, useRef, useState} from "react";
import {Button, Grid, Switch} from "@mui/material";
import AgGridCustomTable from "../../../../../../../util/component/AgGridCustomTable";
import {useForm, FormProvider} from "react-hook-form";
import {useRecoilValue} from "recoil";
import {useTranslation} from "react-i18next";
import {AgGridReact} from "ag-grid-react";
import {CellValueChangedEvent, ColDef, ICellRendererParams} from "ag-grid-community";
import dayjs from "dayjs";
import {generalInfoState} from "../../../../../search-repair-shop/state";
import { ISpecificTimeframe } from "../../../../models";
import FormDatePicker from "../../../../../common/form-fields-wrappers/FormDatePicker";
import CurrentTimeFrame from "./CurrentTimePicker";
import { ISpecificTimeframePanel } from "./types";
import EditSaveButton from "./EditSaveButton";
import {defaultColDef} from './config';
import { createSpecificTimeframe } from "../../../../RsDetail.actions";

const TimeframePanel = ({disabled, setDisabled, curTimeframe, setCurTimeframe}: ISpecificTimeframePanel) => {
    const {t} = useTranslation(["network", "translation"]);
    const generalInfo = useRecoilValue(generalInfoState);
    const timeframePanelSpecificForm = useForm({defaultValues: {id: generalInfo.id, from: null, to: null}})
    const gridRef = useRef<AgGridReact>(null);

    const handleChangeFrom = (newValue: any | null, secondValue: any) => {
        secondValue.setValue(newValue);
    };

    const handleChangeIsOpen = (newValue: any | null, secondValue: any) => {
        secondValue.setValue(newValue.target.checked);
    };

    const columnDefs = useMemo<ColDef[]>(() => [
        {
            field: 'dayOfWeek',
            headerName: t(`rsDetail.timeframe.table.dayOfWeek`),
            valueFormatter: (params) => t(`rsDetail.timeframe.dayOfWeek.${params.value}`)
        },
        {
            field: 'dayOfMonth',
            headerName: t("rsDetail.timeframe.table.date")
        },
        {
            field: 'isOpen',
            headerName: t("rsDetail.timeframe.table.closeOpen"),
            cellRenderer: (params: ICellRendererParams) =>
                <Switch disabled={disabled} onClick={(e: any) => { handleChangeIsOpen(e, params) }} checked={params.value} />,
        },
        {
            field: 'opening',
            headerName: t("rsDetail.timeframe.table.opening"),
            cellRenderer: (p: any) => <CurrentTimeFrame p={p} disabled={disabled} handleChangeForm={handleChangeFrom} />,
        },
        {
            field: 'breakFrom',
            headerName: t("rsDetail.timeframe.table.breakFrom"),
            cellRenderer: (p: any) => <CurrentTimeFrame p={p} disabled={disabled} handleChangeForm={handleChangeFrom} />,
        },
        {
            field: 'breakTo',
            headerName: t("rsDetail.timeframe.table.breakTo"),
            cellRenderer: (p: any) => <CurrentTimeFrame p={p} disabled={disabled} handleChangeForm={handleChangeFrom} />,
        },
        {
            field: 'closing',
            headerName: t("rsDetail.timeframe.table.closing"),
            cellRenderer: (p: any) => <CurrentTimeFrame p={p} disabled={disabled} handleChangeForm={handleChangeFrom} />,
        },
    ], [disabled]);

    const onCellValueChanged = useCallback((params: CellValueChangedEvent) => {
        let changedData = [params.data];
        setCurTimeframe(curTimeframe.map((x) => changedData[0].dayOfMonth === x.dayOfMonth ? changedData[0] : x));
        gridRef.current!.api.applyTransaction({update: curTimeframe.map((x) => changedData[0].dayOfMonth === x.dayOfMonth ? changedData[0] : x)});
    }, [curTimeframe]);

    const onSubmit = (data: any) => {
        createDaysArray(data.from, data.to);
    }

    function createDaysArray(startDate: any, endDate: any) {
        const daysArray = [];
        let currentDate = dayjs(startDate);
        const end = dayjs(endDate).add(1, 'day');
        while (currentDate.isBefore(end)) {
            daysArray.push({
                dayOfWeek: t(`rsDetail.timeframe.dayOfWeek.${currentDate.format('dddd')}`),
                dayOfMonth: currentDate.format('DD-MM-YYYY'),
                isOpen: false,
                opening: null,
                breakFrom: null,
                breakTo: null,
                closing: null
            });

            currentDate = currentDate.add(1, 'day');
        }

        setCurTimeframe(daysArray);
        return daysArray;
    }

    return (
        <>
            <EditSaveButton<ISpecificTimeframe>
                disabled={disabled}
                setDisabled={setDisabled}
                setTimeframe={setCurTimeframe}
                timeframe={curTimeframe}
                timeframeReqField="specificTimeFrame"
                createTimeframe={createSpecificTimeframe}
                title={t("rsDetail.timeframe.tab.specific")}
            />

            <FormProvider {...timeframePanelSpecificForm}>
                <Grid container direction="row" alignItems="start">
                    <Grid container item direction="row" xs={12} columnSpacing={2}>
                        <Grid item xs={3}>
                            <FormDatePicker
                                minDate={dayjs().add(3, 'day')}
                                disabled={disabled}
                                control={timeframePanelSpecificForm.control}
                                name="from"
                                label={t('rsDetail.timeframe.form.label.from')}
                            />
                        </Grid>

                        <Grid item xs={3}>
                            <FormDatePicker
                                minDate={dayjs().add(3, 'day')}
                                disabled={disabled}
                                control={timeframePanelSpecificForm.control}
                                name="to"
                                label={t('rsDetail.timeframe.form.label.to')}
                            />
                        </Grid>
                        <Grid item xs={3} sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                            <Button variant="contained" onClick={timeframePanelSpecificForm.handleSubmit(onSubmit)} disabled={disabled}>
                                {t('rsDetail.timeframe.button.label.apply')}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </FormProvider>
            
            <AgGridCustomTable
                gridRef={gridRef}
                rowData={curTimeframe}
                pagination={false}
                columnDefs={columnDefs}
                containerStyle={{maxHeight: '600px', overflow: 'auto'}}
                onCellValueChanged={onCellValueChanged}
                gridOptions={
                    {
                        defaultColDef,
                        onGridReady: (event) => {
                            event.api.sizeColumnsToFit()
                        }
                    }
                }
            />
        </>
    )
}

export default TimeframePanel;
